// Trello #406 — token balances with a huge number of digits (e.g. max-uint256
// meme tokens) overflow the token-list row. Amounts >= 1B are compacted to a
// short suffix form ("1.15B", "500T"); beyond 999T we fall back to exponent
// notation ("1.16e77") because suffix names stop being recognizable.
// Values below the threshold keep the existing full formatting, so normal
// balances are unaffected.

const COMPACT_THRESHOLD = 1e9
const COMPACT_SUFFIXES = ['K', 'M', 'B', 'T']

/**
 * Returns a compact representation of `value` when it is too large to fit a
 * token-list row, otherwise returns `formatted` (the already-formatted full
 * string) unchanged.
 *
 * `formatted` may be passed as a thunk. Callers building it with
 * `formatDecimals` should prefer that: JS evaluates arguments eagerly, so
 * passing the string directly runs an `Intl.NumberFormat` pass whose result is
 * thrown away for exactly the values that are most expensive to format (a
 * max-uint256 balance grouped into a 100-character string). Deferring it means
 * huge amounts never pay for a representation they don't use.
 */
const compactHugeAmount = (
  value: number | undefined,
  formatted: string | (() => string)
): string => {
  const full = () => (typeof formatted === 'function' ? formatted() : formatted)

  if (typeof value !== 'number' || !Number.isFinite(value)) return full()

  const abs = Math.abs(value)
  if (abs < COMPACT_THRESHOLD) return full()

  const sign = value < 0 ? '-' : ''
  const tier = Math.floor(Math.log10(abs) / 3)

  // Beyond 999T (1e15) — exponent notation, e.g. "1.16e77"
  if (tier >= COMPACT_SUFFIXES.length + 1) {
    return `${sign}${abs.toExponential(2).replace('e+', 'e')}`
  }

  const scaled = abs / 10 ** (tier * 3)
  // Up to 2 decimals, trailing zeros trimmed: 1.5B not 1.50B, 500T not 500.00T
  const scaledFormatted = String(parseFloat(scaled.toFixed(2)))

  return `${sign}${scaledFormatted}${COMPACT_SUFFIXES[tier - 1]}`
}

export default compactHugeAmount
